import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core'
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { ENV, LoggerService, TokenService } from '@bequeathed/ngx-app-core'
import { AIService, AnalyticsEventName, InterviewService, InterviewState } from '@bequeathed/ngx-services'
import { PageUtil, ThreadUtils } from '@bequeathed/ngx-utils'
import { ReplaySubject } from 'rxjs'
import { BaseForm } from '../../../core/ui/base-form/base-form.component'
import { CharityControl } from '../../../core/ui/control/charity-control/charity-control.component'
import { CharityGiftGroupControl } from "../../../core/ui/control/charity-gift-group-control/charity-gift-group-control.component"
import { CharitySearchControl } from '../../../core/ui/control/charity-search-control/charity-search-control.component'
import { CheckboxControl } from '../../../core/ui/control/checkbox-control/checkbox-control.component'
import { ConfirmOptionControl } from '../../../core/ui/control/confirm-option-control/confirm-option-control.component'
import { CurrencyControl } from '../../../core/ui/control/currency-control/currency-control.component'
import { DateControl } from '../../../core/ui/control/date-control/date-control.component'
import { DropdownControl } from '../../../core/ui/control/dropdown-control/dropdown-control.component'
import { EmailControl } from '../../../core/ui/control/email-control/email-control.component'
import { GroupControl } from '../../../core/ui/control/group-control/group-control.component'
import { LinkControl } from "../../../core/ui/control/link-control/link-control.component"
import { NumberControl } from '../../../core/ui/control/number-control/number-control.component'
import { PercentageControl } from '../../../core/ui/control/percentage-control/percentage-control.component'
import { PostcodeControl } from '../../../core/ui/control/postcode-control/postcode-control.component'
import { RadioButtonControl } from '../../../core/ui/control/radio-button-control/radio-button-control.component'
import { TextControl } from '../../../core/ui/control/text-control/text-control.component'
import { VideoControl } from '../../../core/ui/control/video-control/video-control.component'
import { EditConfirmationDialogComponent } from '../../../core/ui/edit-confirmation-dialog/edit-confirmation-dialog.component'
import { AnalyticsUtils } from "../../../core/utils/analytics.utils"
import { QuestionUtils } from "../../../core/utils/question.utils"
import { NavigatorComponent } from '../navigator/navigator.component'

enum InterviewAction {
    start = 'start',
    edit = 'edit',
    editComplete = 'editComplete',
    load = 'load',
    summary = 'summary'
}

const LOG: LoggerService = LoggerService.get('ChatInterfaceComponent')

@Component({
    standalone: true,
    selector: 'app-chat-interface',
    imports: [
    // Angular
    ReactiveFormsModule,
    // Bequeathed
    CharityControl,
    CharitySearchControl,
    CheckboxControl,
    ConfirmOptionControl,
    CurrencyControl,
    DropdownControl,
    EmailControl,
    GroupControl,
    NavigatorComponent,
    NumberControl,
    PercentageControl,
    PostcodeControl,
    RadioButtonControl,
    TextControl,
    VideoControl,
    DateControl,
    LinkControl,
    CharityGiftGroupControl,
    EditConfirmationDialogComponent
],
    templateUrl: './chat-interface.component.html',
    styleUrl: './chat-interface.component.sass'
})
export class ChatInterfaceComponent extends BaseForm implements OnInit {

    aiService: AIService = inject(AIService)
    elementRef: ElementRef = inject(ElementRef)
    interviewService: InterviewService = inject(InterviewService)
    tokenService: TokenService = inject(TokenService)
    route: ActivatedRoute = inject(ActivatedRoute)
    router: Router = inject(Router)
    title: Title = inject(Title)

    action: string | undefined
    interviewId: number | undefined
    questionId: string | null = null
    topicId: string | null = null
    DEBUG = ENV.config.get("show-debug") === "true" || ENV.config.get("show-debug") === true

    @ViewChild('nav') navigator: NavigatorComponent | undefined
    @ViewChild('editConfirm') editConfirm: EditConfirmationDialogComponent | undefined

    stateChanges = new ReplaySubject<InterviewState | null>()

    constructor() {
        super()
    }

    override ngOnInit() {
        super.ngOnInit()
        this.formName = 'AI Interview'
        this.form = new UntypedFormGroup({})

        this.stateChanges.subscribe((state: InterviewState | null) => {
            this.interviewState.set(state)
            QuestionUtils.setUserMetadata(this.interviewState()?.metadata)
            // Funnel stage: interview_page. Every re-rendered question/step is
            // pushed through stateChanges, so this is the single choke point for
            // "a page was viewed" during the forward/edit flow (design §5).
            this.trackInterviewPage(state)
        })

        const url: string = this.router.url
        this.route.params.subscribe((params: Params) => {
            this.type = params['type']
            const queryParams = this.route.snapshot.queryParamMap
            const edit = queryParams.get('edit')
            this.topicId = queryParams.get('topicId')
            this.questionId = queryParams.get('questionId')

            const id = params['id']
            if (id && !isNaN(id)) {
                this.interviewId = id
                if (url.match(/\/interview\/[A-Za-z]+\/[0-9]+\/edit/)) {
                    this.action = InterviewAction.edit
                } else if (edit) {
                    this.action = InterviewAction.editComplete
                    this.enterEditMode(this.topicId!, this.interviewState()?.navigator?.currentTopic ?? null)
                    // editing on a complete interview
                } else {
                    // If no action is present on the URL but an ID is, then assume it's a continuing interview otherwise start a new one
                    this.action = InterviewAction.load
                }
            } else {
                this.action = InterviewAction.start
            }

            this.initInterview().then(() => LOG.debug('init', `Interview initialised! Type: ${this.type}, ID: ${this.interviewId}`))
        })

       this.loading = false
    }

    /**
     * Trigger a custom event to change app branding
     */
    private updateBranding(large = '', small = ''): void {
        this.elementRef.nativeElement.dispatchEvent(
            new CustomEvent('UpdateBrandingEvent', {
                bubbles: true,
                detail: { large, small }
            })
        )
    }

    private async initInterview(): Promise<void> {
        this.form = new UntypedFormGroup({})
        this.loading = true

        const user = this.tokenService.getUser()!

        // Funnel stage: will_interview_launched. Fired once when a brand-new Will
        // interview is launched (the `start` action); continuing/editing an
        // existing interview is not a launch (design §5).
        if (InterviewAction.start === this.action) {
            this.analytics.trackFunnelStage(AnalyticsEventName.WILL_INTERVIEW_LAUNCHED, {
                customer_type: AnalyticsUtils.resolveCustomerType(this.interviewService),
                page: '/interview/WILL/start'
            })
        }

        try {

            if (InterviewAction.load === this.action) {
                this.interviewState.set(await this.aiService.load(`${this.interviewId}`, `${user.userId!}`))
            } else if (InterviewAction.editComplete === this.action) {
                this.interviewState.set(await this.aiService.editInterviewQuestion(
                    `${this.interviewId}`, `${user.userId!}`,
                    false,
                    Object.entries({ editTopicId: this.topicId, editQuestionId: this.questionId })
                ))

            } else {

                if (!this.interviewState() && InterviewAction.edit === this.action) {
                    this.interviewId = (await this.aiService.edit(`${this.interviewId}`, `${user.userId!}`))?.interviewId
                } else if (InterviewAction.editComplete === this.action) {
                    this.interviewState.set(await this.aiService.editInterviewQuestion(
                        `${this.interviewId}`, `${user.userId!}`,
                        false,
                        Object.entries({editTopicId: this.topicId, editQuestionId: this.questionId})
                    ))
                } else {
                    this.interviewId = (await this.aiService.start())?.interviewId
                }

                LOG.debug('initInterview', `Changing URL to [/interview/WILL/${this.interviewId}] so that session is preserved on refresh`)
                if (this.interviewId) {
                    await this.router.navigateByUrl(`/interview/WILL/${this.interviewId}`, { replaceUrl: true })
                    return
                }
            }
        } catch (err) {
            LOG.error(`Error loading the interview! ${err}`)
            await ThreadUtils.sleep(5000)
            this.initInterview().then(() => LOG.debug('initInterview', 'Retrying...'))
            return
        }

        if (this.interviewId) {
            LOG.debug('ChatInterfaceComponent', `Attempting to get the current Question for Interview ${this.interviewId}`)
            if (this.interviewState() && this.interviewState()!.currentQuestion) {
                QuestionUtils.setUserMetadata(this.interviewState()?.metadata)
                LOG.debug('initInterview', `
                    Topic:
                        Name: ${this.interviewState()!.navigator!.currentTopic}
                    Question:
                        ID: ${this.interviewState()!.currentQuestion!.id!}
                        Type: ${this.interviewState()!.currentQuestion!.type!}
                        Required: ${this.interviewState()!.currentQuestion!.required!}
                `)
                this.title.setTitle(this.title.getTitle() + ':' + this.messages.getText(this.interviewState()!.navigator!.currentTopic!))
                // Funnel stage: interview_page for the FIRST rendered question.
                // The initial question is set straight onto interviewState (not
                // pushed through stateChanges), so it is tracked explicitly here.
                this.trackInterviewPage(this.interviewState())
                await this.focusMain()
            } else {
                // FixMe :: Make sure it's time to go to the summary page!
                await this.goToSummary()
            }
        } else {

            // throw new Error("The Interview hasn't been initialised!")
            await ThreadUtils.sleep(2000)
            window.location.reload()
        }
        this.loading = false
    }

    /**
     * Funnel stage: interview_page (design §5). Fires one `interview_page` event
     * per interview page/step actually presented to the user, carrying the step
     * identifier in the `page` field (via {@link AnalyticsService.trackInterviewPage}).
     *
     * The step id is the current question id when present, falling back to the
     * current topic — so the stored `page` identifies which step was viewed. A
     * state with no question to show (e.g. the pre-summary empty state) is not a
     * page view and is skipped. The `customer_type` dimension is stamped so the
     * step counts split across the three journeys (design §5).
     *
     * @param state the interview state about to be/just rendered, or `null`
     */
    private trackInterviewPage(state: InterviewState | null): void {
        const step = state?.currentQuestion?.id
            ?? state?.navigator?.currentTopic
            ?? undefined
        if (!step) {
            return
        }
        this.analytics.trackInterviewPage(step, {
            customer_type: AnalyticsUtils.resolveCustomerType(this.interviewService),
            page: `/interview/WILL/${this.interviewId}`,
            interview_id: this.interviewId ?? '',
            cta_identifier: 'save-and-continue'
        })
    }

    private async focusMain(): Promise<void> {
        await ThreadUtils.sleep(50)
        // Scroll back to the top of the page
        PageUtil.scrollTo('main-focusable')
        PageUtil.setFocus('topicTitle')
    }

    triggerEvaluation(): () => void {
        return () => this.evaluate()
    }

    async evaluate(): Promise<void> {
        if (this.loading) {
            LOG.debug('evaluate', 'Interview is already evaluating, please wait...')
            return
        }

        // Clear existing errors
        this.interviewState()!.errors = undefined

        this.questionControl!.trim()
        await this.revalidateForm()

        // Check if the user has changed their mind
        const changedMindId = Object.keys(this.form.controls).find(key => key.endsWith('changed_mind'));
        const changedMindValue = changedMindId ? this.form.get(changedMindId)?.value : false;

        if (!changedMindValue) {
            // Only validate the form if the user hasn't changed their mind'
            if (this.form.invalid || this.errorMap.size > 0) {
                LOG.debug('evaluate', `Form is still invalid! Errors in form: ${this.errorMap.size}`)
                return
            }
        }

        this.loading = true
        this.questionControl!.applyValue(this.interviewState()!)
        LOG.debug('evaluate', `Answer to be submitted: ${this.interviewState()?.currentQuestion?.userResponse}`)

        // Backward-navigation edit path (task 8.3): when the user is editing an
        // earlier answer, submit via editQuestion (not the forward evaluate) then
        // continue the thread. Delegated so the normal forward flow below stays
        // untouched (Requirement 6.1).
        if (this.editingEarlierAnswer()) {
            await this.saveEditedAnswer()
            return
        }

        if (this.navigator?.currentTopic?.endInterview == true) {
            await this.interviewService.complete(this.type, this.interviewId!, true)
            // Funnel stage: will_interview_cancelled. endInterview flags an early
            // exit, so the Will interview is cancelled on this path (not a normal
            // completion) (design §5).
            this.analytics.trackFunnelStage(AnalyticsEventName.WILL_INTERVIEW_CANCELLED, {
                customer_type: AnalyticsUtils.resolveCustomerType(this.interviewService),
                interview_id: this.interviewId ?? '',
            })
            this.goToHome()
            return
        }

        const newState = await this.aiService.evaluate(this.interviewState()!)
        if (newState?.errors?.fatal || newState?.errors?.validations) {
            const s = this.interviewState()!
            s.errors = newState.errors
            s.currentQuestion = newState.currentQuestion

            this.interviewState.update((state: InterviewState | null) => {
                state!.errors = newState.errors
                return state
            })
            this.changeDetector.detectChanges()

        } else if (newState?.currentQuestion) {
            this.interviewState.set(null)
            this.changeDetector.detectChanges()
            this.stateChanges.next(newState)

        } else {
            return await this.goToSummary()
        }
        this.loading = false
        await this.focusMain()

    }

    /**
     * Save path for the mid-interview backward-navigation edit (task 8.3).
     *
     * Called from {@link evaluate} once the form has been validated and the answer
     * applied, when {@link BaseForm.editingEarlierAnswer} is true. Follows the
     * server contract (design §4.2):
     *
     *  1. Submit the edited answer via {@link AIService.editQuestion} (the
     *     `edit-question` endpoint), NOT the forward `evaluate`. The server
     *     persists the change, re-runs the rules engine, syncs Intercom (incl.
     *     clearing attributes for answers it invalidates) and returns the
     *     re-sequenced {@link InterviewState}.
     *  2. Render whatever the server hands back: if it returns a `currentQuestion`
     *     that is the next thing to ask — either a downstream question the edit
     *     re-opened (Requirement 3.4) or, once continuation resumes, the latest
     *     point. Preserved answers are simply never returned, so they are not
     *     re-asked (Requirement 3.3).
     *  3. Drive continuation via {@link AIService.evaluate} exactly as the forward
     *     flow does — the server dictates when there is nothing left to ask.
     *
     * Re-render mirrors {@link evaluate}: surface validation/fatal errors in place,
     * otherwise clear the signal, run change detection, and push the new state
     * through `stateChanges` so metadata refreshes, then move focus to the heading.
     *
     * Return-to-latest (task 8.4, Requirements 3.5, 3.6). The server continuation is
     * authoritative (design §2, §4.2, §4.5) — the client cannot itself decide which
     * downstream answers an edit invalidates. So:
     *
     *  - **Server returns a `currentQuestion`** — that IS where the user re-joins
     *    the flow: either a downstream question the edit re-opened (Requirement 3.4)
     *    or, once the re-ask thread is exhausted, the latest outstanding question
     *    (the point they were at before jumping back). We render it and
     *    {@link BaseForm.exitEditMode}, so the next "Save & continue" runs the normal
     *    forward {@link evaluate} path (design §4.2: `editQuestion` for the first
     *    edited question, `evaluate` for subsequent ones). This also covers the
     *    no-op / unchanged submit (Requirement 3.6): the server returns the existing
     *    latest question with downstream answers intact and we simply render it.
     *  - **Server returns no `currentQuestion`** — only genuine completion. Exit edit
     *    mode and route to the summary (design §4.2: "or routes to summary if none";
     *    Requirement 6.2 keeps completion behaviour unchanged).
     *
     * Whole-topic walk (design §4.2, backend `nextQuestionInTopicForEdit`). Each save
     * submits with `walkTopic=true`. While the server keeps returning questions that
     * belong to the edited topic (`navigator.currentTopic === editTopicId`), we remain
     * in edit mode so every question in the topic is re-presented pre-filled. Once the
     * server returns a question outside the edited topic, the walk is exhausted and the
     * forward flow has resumed at the latest point, so we exit edit mode. We do not
     * re-walk unaffected topics or reload the interview; the server drives sequencing.
     */
    private async saveEditedAnswer(): Promise<void> {
        const editTopicId = this.editTopicId()
        LOG.debug('saveEditedAnswer', `Submitting edited answer for topic [${editTopicId}] via editQuestion (walkTopic)`)

        // walkTopic=true asks the server to re-present EVERY question in the edited
        // topic, pre-filled, before resuming the forward flow (design §4.2). The
        // whole topic is walked one question per Save & continue.
        const newState = await this.aiService.editQuestion(this.interviewState()!, true)

        if (newState?.errors?.fatal || newState?.errors?.validations) {
            // Stay on the edited question and surface the errors in place; remain in
            // edit mode so the corrected re-submit still routes through editQuestion.
            this.interviewState.update((state: InterviewState | null) => {
                state!.errors = newState.errors
                state!.currentQuestion = newState.currentQuestion
                return state
            })
            this.changeDetector.detectChanges()

        } else if (newState?.currentQuestion) {
            // Two cases distinguished by the topic the server hands back:
            //
            //  - Still WITHIN the edited topic (currentTopic === editTopicId): the
            //    server is walking the next question in this topic. STAY in edit mode
            //    so the next Save & continue keeps routing through editQuestion with
            //    walkTopic=true (Requirement 3.3).
            //  - OUTSIDE the edited topic: the topic walk is exhausted and the server
            //    has resumed the forward flow at the latest point (Requirements 3.4,
            //    3.5, 3.6). Exit edit mode so continuation uses the forward evaluate().
            const stillWalkingTopic = !!editTopicId && newState.navigator?.currentTopic === editTopicId
            if (!stillWalkingTopic) {
                this.exitEditMode()
            }
            this.interviewState.set(null)
            this.changeDetector.detectChanges()
            this.stateChanges.next(newState)

        } else {
            // No further question: the interview is genuinely complete. Leave edit
            // mode and route to the summary, matching the forward flow (Requirement 6.2).
            this.exitEditMode()
            return await this.goToSummary()
        }

        this.loading = false
        await this.focusMain()
    }

    doNothing() {
        LOG.trace('doNothing', 'Preventing form submission')
    }

    async saveAndExit(): Promise<void> {
        LOG.debug('saveAndExit', 'Interview saved, redirecting to vault...')
        this.analytics.trackFunnelStage(AnalyticsEventName.WILL_INTERVIEW_EXIT, {
            customer_type: AnalyticsUtils.resolveCustomerType(this.interviewService),
            interview_id: this.interviewId ?? '',
            page: `/interview/WILL/${this.interviewId}`,
            cta_identifier: 'exit',
        })
        this.goToWills()
    }

    goToHome(): void {
        const homeApp = ENV.applications.get('HOME')!
        window.location.href = homeApp.baseUrl
    }

    goToWills(): void {
        const homeApp = ENV.applications.get('HOME')!
        window.location.href = `${homeApp.baseUrl}${homeApp.api.get('dashboard')}`
    }

    async goToSummary(): Promise<void> {
        LOG.debug('evaluate', 'The AI Service hasn\'t provided a new question - Redirecting to the Summary')
        await this.router.navigateByUrl(`/interview/WILL/${this.interviewId}/${this.interviewState()!.sessionId!}/summary`, { replaceUrl: true })
        return
    }

    /**
     * Feature flag guarding mid-interview backward navigation.
     *
     * Read from the runtime config map (`ENV.config`), mirroring the same flag the
     * {@link NavigatorComponent} uses to gate rendering. Even though the navigator
     * only emits `jumpToTopic` when the flag is on, the handler re-checks it so the
     * wiring is safe if the event is ever raised while the feature is off (task 8).
     *
     * Temporary; removed once the feature is enabled by default (task 12).
     */
    get backwardNavigationEnabled(): boolean {
        return ENV.config.get('backward-navigation-enabled') === true
            || ENV.config.get('backward-navigation-enabled') === 'true'
    }

    /**
     * Handle a `jumpToTopic` request from the navigator (task 8.1).
     *
     * Flow (Requirements 2.2, 3.1):
     *  1. Show the pre-edit confirmation dialog. On dismiss, do nothing — the dialog
     *     itself returns focus to the originating control (Requirement 2.3), so we
     *     stay exactly where we were.
     *  2. On confirm, enter edit mode ({@link BaseForm.enterEditMode}) and call
     *     {@link AIService.editInterviewQuestion} (`edit-interview-question` →
     *     WillsAIService.loadEditQuestion) keyed by `editTopicId` for ALL topics.
     *     The server resolves the topic's first question and returns a real
     *     re-sequenced state; GROUP vs non-GROUP no longer selects the endpoint.
     *  3. If the returned state has a `currentQuestion`, render it in the existing
     *     chat interface. Otherwise (null / empty state) leave edit mode and bail
     *     without blanking the UI, so we never get stuck loading (NG0100).
     *
     * Gated behind the feature flag so the handler is a no-op if the event is ever
     * raised while backward navigation is off.
     *
     * @param editTopicId the target `Topic.id` emitted by the navigator
     */
    async onJumpToTopic(editTopicId: string): Promise<void> {
        if (!this.backwardNavigationEnabled) {
            LOG.debug('onJumpToTopic', 'Backward navigation is disabled; ignoring jump request')
            return
        }
        if (!editTopicId) {
            LOG.warn('onJumpToTopic: No topic id supplied; ignoring jump request')
            return
        }

        LOG.debug('onJumpToTopic', `Requested return to topic [${editTopicId}]`)

        // Capture the "latest" / current outstanding topic BEFORE entering edit
        // mode. When NOT already editing, navigator.currentTopic is the latest
        // outstanding topic (the point to return to). If we ARE already editing,
        // navigator.currentTopic is the EDIT topic (the server sets it to the topic
        // being edited), so preserve the previously captured latest instead of
        // overwriting it with an edit topic.
        const latestTopicId: string | null = this.editingEarlierAnswer()
            ? this.latestTopicId()
            : (this.interviewState()?.navigator?.currentTopic ?? null)

        // Return-to-latest (spec "return to latest"): jumping to the captured latest
        // topic means "go back to where I was, without changing anything". This is a
        // no-op edit, so we DO NOT show the pre-edit confirmation and DO NOT enter an
        // edit — we simply reload the interview to its latest outstanding question.
        if (latestTopicId && editTopicId === latestTopicId) {
            await this.returnToLatest()
            return
        }

        // The control that opened the dialog, so focus can be returned to it on dismiss.
        const originatingElement = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : undefined

        const confirmed: boolean = (await this.editConfirm?.open({ originatingElement })) === true
        if (!confirmed) {
            // Dismiss: no state change; the dialog handles returning focus (Requirement 2.3).
            LOG.debug('onJumpToTopic', 'User dismissed the edit confirmation; staying put')
            return
        }

        const user = this.tokenService.getUser()!
        this.loading = true

        try {
            this.enterEditMode(editTopicId, latestTopicId)

            // Route EVERY topic jump through editInterviewQuestion (`edit-interview-question` →
            // WillsAIService.loadEditQuestion), the endpoint that returns a real re-sequenced state
            // for a topic keyed by `editTopicId`. GROUP vs non-GROUP no longer picks the endpoint —
            // editSummaryQuestion (`edit-will-interview`) only logs an Intercom event and returns an
            // empty shell state, which left the UI stuck loading (Requirement 3.1).
            const editState: InterviewState | null = await this.aiService.editInterviewQuestion(
                `${this.interviewId}`, `${user.userId!}`,
                false,
                Object.entries({ editTopicId })
            )

            // Defensive guard: if the server hands back nothing usable (null state, or a state with no
            // currentQuestion), do NOT blank the interface. Blanking (interviewState.set(null) +
            // stateChanges.next) with no question leaves the loading branch showing forever and triggers
            // NG0100. Instead log, leave edit mode, and bail without re-rendering.
            if (!editState || !editState.currentQuestion) {
                LOG.error(`onJumpToTopic: Edit endpoint returned no usable state (no currentQuestion) for topic [${editTopicId}]`)
                this.exitEditMode()
                return
            }

            // Render the target topic in the existing chat interface. Mirror the
            // evaluate() re-render pattern: clear then push through stateChanges so
            // QuestionUtils metadata is refreshed.
            this.interviewState.set(null)
            this.changeDetector.detectChanges()
            this.stateChanges.next(editState)

            // Move focus to the loaded question heading (task 8.2, Requirement 7.3).
            // Reuse focusMain() — the same PageUtil.scrollTo('main-focusable') +
            // PageUtil.setFocus('topicTitle') pattern evaluate() runs after a
            // re-render — so it only fires once the new state has rendered.
            await this.focusMain()

            // On save, evaluate() branches on editingEarlierAnswer() and delegates
            // to saveEditedAnswer() — submit via editQuestion, render the
            // re-sequenced state, and on completion / no-op return the user to the
            // latest point via exitEditMode() so continuation resumes the forward
            // evaluate() flow (task 8.4, Requirements 3.5, 3.6).
        } catch (err) {
            LOG.error(`onJumpToTopic: Error loading the edit target topic! ${err}`)
            this.exitEditMode()
        } finally {
            this.loading = false
        }
    }

    /**
     * Return the user to the CURRENT / LATEST topic they were on before jumping
     * back, without making any change (spec "return to latest").
     *
     * The latest topic is the outstanding (not-yet-answered) topic, so there is no
     * answer to edit — this is a pure navigation back to where the user was. Because
     * nothing changes:
     *  - the pre-edit confirmation dialog is SKIPPED (it only warns that later
     *    answers may be re-asked when EDITING; returning to latest re-asks nothing);
     *  - we leave edit mode ({@link BaseForm.exitEditMode}) and reload the interview
     *    to its latest outstanding question via {@link AIService.load}, rather than
     *    calling the edit endpoint.
     *
     * Render mirrors the edit-load / evaluate re-render pattern: clear the signal,
     * run change detection, push the reloaded state through `stateChanges`, and move
     * focus to the heading. The same defensive guard is used: if `load` returns
     * nothing usable (null state / no currentQuestion), log and bail WITHOUT blanking
     * the interface (avoids a stuck "Loading..." / NG0100).
     */
    private async returnToLatest(): Promise<void> {
        LOG.debug('returnToLatest', 'Returning to the latest outstanding topic without changes (no edit, no confirmation)')

        const user = this.tokenService.getUser()!
        this.loading = true

        try {
            // Leave edit mode first: returning to latest is not an edit, so the
            // banner/endpoint selection must reflect the normal forward flow.
            this.exitEditMode()

            const latestState: InterviewState | null = await this.aiService.load(
                `${this.interviewId}`, `${user.userId!}`
            )

            // Defensive guard, identical to the edit-load path: never blank the UI
            // when the server hands back nothing usable.
            if (!latestState || !latestState.currentQuestion) {
                LOG.error('returnToLatest: load returned no usable state (no currentQuestion); bailing without blanking')
                return
            }

            // Re-render via the existing pattern so QuestionUtils metadata refreshes.
            this.interviewState.set(null)
            this.changeDetector.detectChanges()
            this.stateChanges.next(latestState)

            // Move focus to the loaded question heading (Requirement 7.3).
            await this.focusMain()
        } catch (err) {
            LOG.error(`returnToLatest: Error reloading the interview to its latest question! ${err}`)
        } finally {
            this.loading = false
        }
    }
}
